import { competenceDomainKeys } from '@/composables/competenceDomain.keys';
import { observationTimelineKeys } from '@/composables/observationTimeline/observationTimeline.keys';
import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { useCurrentSchoolSubject } from '@/composables/useCurrentSchoolSubject';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { sortCompetenceDomains } from '@/utils/competence.domain.utils';
import { FilterOperationType, IFilter } from '@chax-at/prisma-filter-common';
import { useQueryClient } from '@tanstack/vue-query';
import { ICompetenceDomainFilter } from 'common/interface/filter.interface';
import { toRecord } from 'common/utils/array.utils';
import { computed, ComputedRef } from 'vue';

interface ICompetenceDomainComposable {
  allCompetenceDomains: ComputedRef<Components.Schemas.CompetenceDomainDto[]>;
  allCompetenceDomainTemplatesForSubjectTemplate: ComputedRef<Components.Schemas.CompetenceDomainDto[]>;
  competenceDomainsForSchoolSubject: ComputedRef<Components.Schemas.CompetenceDomainDto[]>;
  competenceById: ComputedRef<Partial<Record<number, Components.Schemas.CompetenceDto>>>;
  competenceDomainById: ComputedRef<Partial<Record<number, Components.Schemas.CompetenceDomainDto>>>;
  isLoading: ComputedRef<boolean>;
  createCompetenceDomain: (schoolSubjectId: number, dto: Components.Schemas.CreateCompetenceDomainDto) => Promise<void>;
  deleteCompetenceDomain: (domainId: number) => Promise<void>;
  editCompetenceDomain: (domainId: number, data: Components.Schemas.UpdateCompetenceDomainDto) => Promise<void>;
  getCompetenceDomainForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => Components.Schemas.CompetenceDomainDto | null;
  getCompetenceForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => Components.Schemas.CompetenceDto | null;
  refresh: () => Promise<void>;
  createCompetenceDomainTemplate: (
    schoolSubjectId: number,
    dto: Components.Schemas.CreateCompetenceDomainDto,
  ) => Promise<void>;
}

export const useCompetenceDomain = (): ICompetenceDomainComposable => {
  const { currentSchoolSubject, currentSchoolSubjectTemplate } = useCurrentSchoolSubject();
  const { currentClass } = useCurrentClass();
  const { createSuccessToast, editSuccessToast, deleteSuccessToast, catchError } = useToast();

  const getCompetenceDomainsForCurrentClass = async (): Promise<Components.Schemas.CompetenceDomainDto[]> => {
    if (!currentClass.value?.id) return [];
    const filter: IFilter<ICompetenceDomainFilter> = {
      filter: [
        { field: 'isTemplate', type: FilterOperationType.Eq, value: false },
        { field: 'schoolSubject.schoolClassId', type: FilterOperationType.Eq, value: currentClass.value.id },
      ],
      order: [
        { field: 'name', dir: 'asc' },
        { field: 'createdAt', dir: 'desc' },
      ],
    };
    const result = await Api.Routes.CompetenceDomain.retrieve({
      $filter: filter,
    });
    return result.rows;
  };

  const getCompetenceDomainTemlatesForCurrentSubject = async (): Promise<Components.Schemas.CompetenceDomainDto[]> => {
    if (!currentSchoolSubjectTemplate.value?.id) return [];
    const filter: IFilter<ICompetenceDomainFilter> = {
      filter: [
        { field: 'isTemplate', type: FilterOperationType.Eq, value: true },
        { field: 'schoolSubjectId', type: FilterOperationType.Eq, value: currentSchoolSubjectTemplate.value.id },
      ],
      order: [{ field: 'name', dir: 'asc' }],
    };
    const result = await Api.Routes.CompetenceDomain.retrieve({
      $filter: filter,
    });
    return result.rows;
  };

  const competenceDomainListQueryKey = computed(() => competenceDomainKeys.classList(currentClass.value?.id));
  const queryClient = useQueryClient();
  const query = useQueryWithToast({
    queryKey: competenceDomainListQueryKey,
    queryFn: getCompetenceDomainsForCurrentClass,
    select: sortCompetenceDomains<Components.Schemas.CompetenceDomainDto>,
  });

  const templateQueryKey = computed(() => competenceDomainKeys.templates(currentSchoolSubjectTemplate.value?.id));
  const templateQuery = useQueryWithToast({
    queryKey: templateQueryKey,
    queryFn: getCompetenceDomainTemlatesForCurrentSubject,
    select: sortCompetenceDomains<Components.Schemas.CompetenceDomainDto>,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: observationTimelineKeys.lists(currentClass.value?.id) });
    await queryClient.invalidateQueries({ queryKey: competenceDomainListQueryKey });
    await queryClient.invalidateQueries({ queryKey: templateQueryKey });
    // the flowers' competence dialogs keep single domains, which an edit of a domain or competence outdates
    await queryClient.invalidateQueries({ queryKey: competenceDomainKeys.details() });
  };

  const isLoading = computed(
    () =>
      query.isLoading.value ||
      query.isFetching.value ||
      templateQuery.isLoading.value ||
      templateQuery.isFetching.value,
  );

  const createCompetenceDomain = async (
    schoolSubjectId: number,
    dto: Components.Schemas.CreateCompetenceDomainDto,
  ): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.CompetenceDomain.create({ schoolSubjectId }, dto);
      await refresh();
      createSuccessToast();
    });
  };

  const createCompetenceDomainTemplate = async (
    schoolSubjectId: number,
    dto: Components.Schemas.CreateCompetenceDomainDto,
  ): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.CompetenceDomain.createTemplate({ schoolSubjectId }, dto);
      await refresh();
      createSuccessToast();
    });
  };

  const deleteCompetenceDomain = async (domainId: number): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.CompetenceDomain.delete({ domainId });
      await refresh();
      deleteSuccessToast();
    });
  };

  const editCompetenceDomain = async (
    domainId: number,
    data: Components.Schemas.UpdateCompetenceDomainDto,
  ): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.CompetenceDomain.update({ domainId }, data);
      await refresh();
      editSuccessToast();
    });
  };

  const allCompetenceDomains = computed<Components.Schemas.CompetenceDomainDto[]>(() => {
    return query.data.value ?? [];
  });

  const allCompetenceDomainTemplatesForSubjectTemplate = computed<Components.Schemas.CompetenceDomainDto[]>(() => {
    return templateQuery.data.value ?? [];
  });

  const competenceDomainsForSchoolSubject = computed<Components.Schemas.CompetenceDomainDto[]>(() => {
    return allCompetenceDomains.value.filter((domain) => domain.schoolSubjectId === currentSchoolSubject.value?.id);
  });

  const competenceById = computed<Partial<Record<number, Components.Schemas.CompetenceDto>>>(() => {
    const competences = allCompetenceDomains.value.flatMap((domain) => domain.competences);
    return toRecord(
      competences,
      (competence) => competence.id,
      (competence) => competence,
    );
  });

  const competenceDomainById = computed<Partial<Record<number, Components.Schemas.CompetenceDomainDto>>>(() => {
    return toRecord(
      allCompetenceDomains.value,
      (domain) => domain.id,
      (domain) => domain,
    );
  });

  const getCompetenceForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): Components.Schemas.CompetenceDto | null => {
    const competence = competenceById.value[observation.competenceId];
    return competence ?? null;
  };

  const getCompetenceDomainForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): Components.Schemas.CompetenceDomainDto | null => {
    const competence = getCompetenceForObservation(observation);
    const domain = competence ? competenceDomainById.value[competence.domainId] : null;
    return domain ?? null;
  };

  return {
    allCompetenceDomains,
    allCompetenceDomainTemplatesForSubjectTemplate,
    competenceDomainsForSchoolSubject,
    competenceById,
    competenceDomainById,
    isLoading,
    createCompetenceDomain,
    deleteCompetenceDomain,
    editCompetenceDomain,
    getCompetenceDomainForObservation,
    getCompetenceForObservation,
    refresh,
    createCompetenceDomainTemplate,
  };
};
